/**
 * OMS 2 Tees / 3 Tees Bundle Builder — tiered offer.
 *
 * Offer architecture (mutually exclusive tiers — carried forward from a
 * proven reconciliation architecture used on an earlier campaign, extended
 * for tiering):
 *   TIER "towel": 2 qualifying Tees/Tanks (tee1 + tee2) → Unicorn auto-adds
 *     a free Gym Towel gift line. No hat involved.
 *   TIER "hat": 3 qualifying Tees/Tanks (tee1 + tee2 + tee3) + 1
 *     customer-selected Trucker Hat → Unicorn makes the selected hat free.
 *     The Gym Towel must NOT apply at this tier — see Unicorn cart config
 *     (kept in Draft; see project notes for exact tier setup + the specific
 *     mutual-exclusivity question that could not be confirmed via Unicorn's
 *     admin UI this round).
 *   The tier is driven entirely by whether the customer has chosen Tee/Tank
 *     3: choosing it enters the hat tier; there is an explicit "Skip — keep
 *     my Gym Towel" affordance to back out to the towel tier. Never both.
 *
 * Cart ownership: this script owns ONLY tee1/tee2/tee3/hat lines, tagged
 * `_oms_3tee_bundle: '3-tees-free-hat-towel'` / `_oms_3tee_role`. These
 * property names and tag value are deliberately NOT renamed as part of the
 * 2-Tee/3-Tee naming cleanup (unlike every other oms-3tee/oms_3tee
 * identifier in this codebase) — they're already-written data on any real
 * or QA-tester cart line that predates the rename. Changing the tag value
 * would make this reconciliation logic stop recognizing those existing
 * lines as its own, leaving them stranded in a cart instead of being
 * managed correctly. Treat them as a fixed internal/legacy identifier,
 * independent of the campaign's public naming. It never adds/removes the
 * Gym Towel — that's entirely Unicorn's job. Reconciliation is tier-aware:
 * whichever roles are NOT part of the customer's current tier are treated
 * as stale and removed on the next Add/Update click (e.g. dropping from
 * the hat tier back to the towel tier removes tee3 + hat).
 */
(function () {
  'use strict';

  var initializedRoots = new WeakSet();
  var BUNDLE_TAG = '3-tees-free-hat-towel';
  var ALL_ROLES = ['tee1', 'tee2', 'tee3', 'hat'];

  function bundleLineProps(role) {
    return { _oms_3tee_bundle: BUNDLE_TAG, _oms_3tee_role: role };
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  async function fetchCart() {
    var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Could not read your cart.');
    return res.json();
  }

  async function changeCartLine(key, quantity) {
    var res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
    if (!res.ok) {
      var errBody = await res.json().catch(function () { return {}; });
      throw new Error(errBody.message || errBody.description || 'Could not update your bundle in cart.');
    }
    return res.json();
  }

  // Groups this bundle's own tagged cart lines by role. Untagged lines (a
  // customer's own independently-added products, or any other campaign's
  // lines) are never touched — only lines whose
  // properties._oms_3tee_bundle matches BUNDLE_TAG are ever considered. A
  // tagged line with an unrecognized/missing role is treated as obsolete and
  // removed.
  function collectBundleLines(cart) {
    var byRole = { tee1: [], tee2: [], tee3: [], hat: [] };
    var obsolete = [];
    (cart.items || []).forEach(function (item) {
      var props = item.properties || {};
      if (props._oms_3tee_bundle !== BUNDLE_TAG) return;
      var role = props._oms_3tee_role;
      if (Object.prototype.hasOwnProperty.call(byRole, role)) {
        byRole[role].push(item);
      } else {
        obsolete.push(item);
      }
    });
    return { byRole: byRole, obsolete: obsolete };
  }

  // Diffs the customer's CURRENT tier selections against the CURRENT
  // (freshly-fetched) cart's bundle-owned lines. `desired` only contains the
  // roles relevant to the customer's CURRENT tier (e.g. just {tee1, tee2}
  // for the towel tier) — any of our own tagged lines for a role NOT present
  // in `desired` are treated as stale (tier was downgraded since they were
  // added) and queued for removal, exactly like an obsolete line.
  function planReconciliation(cart, desired) {
    var collected = collectBundleLines(cart);
    var removeKeys = [];
    var addItems = [];
    var fixQuantityKeys = [];

    ALL_ROLES.forEach(function (role) {
      var lines = collected.byRole[role];
      var desiredId = Object.prototype.hasOwnProperty.call(desired, role) ? desired[role] : null;

      if (desiredId === null) {
        // Role not part of the current tier — any existing line(s) for it
        // are stale (most commonly: customer downgraded from the hat tier
        // back to the towel tier since they were added).
        lines.forEach(function (l) { removeKeys.push(l.key); });
        return;
      }

      if (lines.length === 0) {
        addItems.push({ id: desiredId, quantity: 1, properties: bundleLineProps(role) });
        return;
      }
      var canonical = lines[0];
      lines.slice(1).forEach(function (extra) { removeKeys.push(extra.key); });
      if (String(canonical.variant_id) !== String(desiredId)) {
        removeKeys.push(canonical.key);
        addItems.push({ id: desiredId, quantity: 1, properties: bundleLineProps(role) });
      } else if (canonical.quantity !== 1) {
        fixQuantityKeys.push(canonical.key);
      }
      // else: unchanged, correct variant, quantity 1 — leave alone.
    });

    collected.obsolete.forEach(function (l) { removeKeys.push(l.key); });

    return { removeKeys: removeKeys, addItems: addItems, fixQuantityKeys: fixQuantityKeys };
  }

  function initBuilder(root) {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);

    var sectionId = root.id.replace('oms-2tee-3tee-builder-', '');
    var config = (window.oms2tee3teeBundleConfig && window.oms2tee3teeBundleConfig[sectionId]) || {};
    var labels = config.labels || {};
    var towel = config.towel || {};

    var slots = {
      tee1: { productId: null, variant: null, title: null },
      tee2: { productId: null, variant: null, title: null },
      tee3: { productId: null, variant: null, title: null },
      hat: { productId: null, variant: null, title: null },
    };

    // One controller per rail (populated by setupCoverflow below), used
    // only by the Skip handler to suspend a rail's in-flight commit — see
    // the comment on `suspend` in setupCoverflow for why this exists.
    var railControllers = {};
    var cartInProgress = false;
    var hasExistingBundleLines = false;
    // Fires exactly once, the first time Tee/Tank 3 becomes reachable
    // (towel tier completes) — see the trigger in selectVariant(). Never
    // reset: unlike the hat tier (which has an explicit Skip-driven "exit"
    // that should re-arm its own auto-select on re-entry), there's no
    // equivalent "exit step 3" action for tee1/tee2 to hook, and tying this
    // to a towelTierComplete() true/false transition instead would
    // misfire — swapping tee1 or tee2 to a *different* product briefly
    // clears that product's variant, which flips towelTierComplete() back
    // to false and then true again the moment its new size is picked, well
    // after the customer has already made their own tee3 choice.
    var tee3AutoRevealed = false;

    var summaryEl = document.getElementById('oms-2tee-3tee-summary-' + sectionId);
    var stickyBarEl = document.getElementById('oms-2tee-3tee-sticky-bar-' + sectionId);
    var rewardPanelEl = document.getElementById('oms-2tee-3tee-reward-panel-' + sectionId);
    var tee3SectionEl = document.getElementById('oms-2tee-3tee-tee3-section-' + sectionId);
    var hatSectionEl = document.getElementById('oms-2tee-3tee-hat-section-' + sectionId);

    // --- Tier logic -------------------------------------------------------
    // Entering the hat tier is driven solely by whether Tee/Tank 3 has been
    // chosen (a product picked, even before its size is set — the tier
    // switches and the hat rail reveals itself as soon as the customer
    // commits to a 3rd item, matching "the offer state changes" language).
    function inHatTier() {
      return Boolean(slots.tee3.productId);
    }
    function towelTierComplete() {
      return Boolean(slots.tee1.variant && slots.tee2.variant);
    }
    function hatTierComplete() {
      return Boolean(slots.tee1.variant && slots.tee2.variant && slots.tee3.variant && slots.hat.variant);
    }
    function isReady() {
      if (cartInProgress) return false;
      return inHatTier() ? hatTierComplete() : towelTierComplete();
    }
    function desiredSelection() {
      if (inHatTier()) {
        return { tee1: slots.tee1.variant && slots.tee1.variant.id, tee2: slots.tee2.variant && slots.tee2.variant.id, tee3: slots.tee3.variant && slots.tee3.variant.id, hat: slots.hat.variant && slots.hat.variant.id };
      }
      return { tee1: slots.tee1.variant && slots.tee1.variant.id, tee2: slots.tee2.variant && slots.tee2.variant.id };
    }

    // Checked on load (and again after every successful reconcile) so the
    // CTA can prefer "Update Bundle" over "Add Bundle to Cart" when the cart
    // already holds this bundle's lines. Re-derived from a fresh cart fetch
    // every time — never a one-way latch.
    function refreshBundleLinePresence() {
      fetchCart()
        .then(function (cart) {
          hasExistingBundleLines = (cart.items || []).some(function (item) {
            return item.properties && item.properties._oms_3tee_bundle === BUNDLE_TAG;
          });
          render();
        })
        .catch(function () {
          /* non-fatal; keep default label */
        });
    }

    function railEl(key) {
      return root.querySelector('[data-oms-2tee-3tee-rail="' + key + '"]');
    }

    // ---------------------------------------------------------------------
    // Coverflow: centers the rail on whichever card is nearest the rail
    // wrap's horizontal midpoint, scales it up (.is-focused), and — once
    // scrolling settles for ~130ms — commits that card as the role's active
    // selection (reveals its sizes, updates the summary).
    // Previously, revealing a card's size row (and writing its
    // productId/title into `slots`) was gated behind a 130ms
    // settle-timer that only fired once scrolling had fully stopped, while
    // the purely-visual `.is-focused` scale-up repainted on every animation
    // frame. That created a real window — confirmed via direct
    // elementFromPoint hit-testing during a QA pass — where a card looked
    // centered/settled well before its size row actually existed or had
    // been updated: a tap landing in that window either hit whatever
    // adjacent content had risen up to fill the not-yet-revealed row's
    // space (silently doing nothing), or, on a second/third swipe within
    // the same step, hit a row still showing the *previous* card's sizes
    // (silently selecting the wrong product). Both are fixed the same way:
    // the reveal/commit now runs on the exact same per-frame cadence as the
    // visual focus update, so there is no longer any gap between "looks
    // centered" and "is centered" for the customer to tap into.
    function setupCoverflow(key) {
      var rail = railEl(key);
      if (!rail || rail.dataset.coverflowInit) return;
      rail.dataset.coverflowInit = 'true';
      var wrap = rail.closest('.oms-2tee-3tee-rail-wrap');
      var cards = Array.prototype.slice.call(rail.querySelectorAll('[data-oms-2tee-3tee-tee-card]'));
      var ticking = false;
      var hasInteracted = false;

      function findClosest() {
        var wrapRect = wrap.getBoundingClientRect();
        var centerX = wrapRect.left + wrapRect.width / 2;
        var closest = null;
        var closestDist = Infinity;
        cards.forEach(function (card) {
          var r = card.getBoundingClientRect();
          var cx = r.left + r.width / 2;
          var d = Math.abs(cx - centerX);
          if (d < closestDist) {
            closestDist = d;
            closest = card;
          }
        });
        return closest;
      }

      function paintFocus(closest) {
        cards.forEach(function (card) {
          card.classList.toggle('is-focused', card === closest);
        });
      }

      function commit(target) {
        var closest = target || findClosest();
        paintFocus(closest);
        if (!hasInteracted || !closest || closest.disabled) return;
        selectCard(key, closest);
      }

      // Fires at most once per rendered frame (the `ticking` guard
      // coalesces however many 'scroll' events land within that frame) —
      // this is the SAME cadence the visual focus repaint already used, so
      // folding the reveal/commit into it costs nothing extra in practice
      // and removes the settle-timer gap entirely rather than just
      // shortening it.
      rail.addEventListener('scroll', function () {
        hasInteracted = true;
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(function () {
            commit();
            ticking = false;
          });
        }
      }, { passive: true });

      // Tap-to-select: commits the tapped card immediately (unambiguous
      // intent — no reason to wait on anything for a direct tap) and also
      // smooth-scrolls it to center as visual confirmation. The scroll is
      // driven by a manually computed scrollTo() rather than
      // card.scrollIntoView(): scrollIntoView's "nearest visible"
      // heuristics fight with scroll-snap-type: mandatory on this
      // wide-padded rail, so it would frequently under-scroll. Committing
      // immediately also sidesteps the case where the tapped card is
      // already at its resting/centered position — scrollTo() to an
      // unchanged position fires no 'scroll' event at all, so the
      // scroll-driven commit path alone would never fire for that tap.
      cards.forEach(function (card) {
        card.addEventListener('click', function () {
          if (card.disabled) return;
          hasInteracted = true;
          commit(card);
          var wrapRect = wrap.getBoundingClientRect();
          var cardRect = card.getBoundingClientRect();
          var delta = (cardRect.left + cardRect.width / 2) - (wrapRect.left + wrapRect.width / 2);
          if (Math.abs(delta) > 1) {
            rail.scrollTo({ left: rail.scrollLeft + delta, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
          }
        });

        // Tapping the product image opens the zoom lightbox instead of
        // selecting/centering the card — stopPropagation keeps this click
        // from ever reaching the card's own listener above, so opening the
        // lightbox can never change which product/size is selected or move
        // the coverflow. The lightbox itself (initImageZoomModal) is a
        // fully separate, self-contained component that never touches
        // `slots`, cart, or reconciliation state.
        var zoomTrigger = card.querySelector('[data-oms-2tee-3tee-zoom-trigger]');
        if (zoomTrigger) {
          zoomTrigger.addEventListener('click', function (e) {
            e.stopPropagation();
            if (card.disabled) return;
            var modal = document.getElementById('oms-2tee-3tee-image-zoom-modal-' + sectionId);
            if (!modal || typeof modal._omsOpen !== 'function') return;
            var images = [];
            try {
              images = JSON.parse(zoomTrigger.getAttribute('data-images') || '[]');
            } catch (err) {
              images = [];
            }
            modal._omsOpen(images, zoomTrigger);
          });
        }
      });

      // Exposed so an explicit external reset (currently only the Skip
      // handler, for the tee3/hat rails) can suspend this rail's commit
      // mechanism. Without this: tapping/swiping a card queues a
      // requestAnimationFrame-driven commit that keeps firing for as long
      // as the rail's native momentum/snap-settle continues generating
      // 'scroll' events — on a real phone that can trail a couple hundred
      // ms past the point the card visually looks settled. If the customer
      // taps Skip inside that window, Skip's synchronous reset of
      // slots.tee3/slots.hat was being silently overwritten a moment later
      // by one of those still-in-flight commits re-selecting the same
      // card, flipping the reward panel back to the hat tier even though
      // Skip had just correctly rendered the towel tier. Calling
      // suspend() makes `commit()`'s `if (!hasInteracted ...) return;`
      // guard reject any such late-arriving commit until the customer
      // actually interacts with this rail again.
      railControllers[key] = {
        suspend: function () { hasInteracted = false; },
      };

      // Focus (not select) the first card by default so every rail starts
      // on a clean, deliberate-looking state instead of appearing with no
      // card highlighted until the customer first touches it. paintFocus()
      // only toggles the `.is-focused` CSS class — it never calls
      // selectCard(), so nothing is written to `slots`, no size row reveals,
      // and no product is silently pre-selected on the customer's behalf.
      if (cards.length) paintFocus(cards[0]);
    }

    // Auto-reveals/selects the currently-centered (first, at call time)
    // card of a rail without requiring a tap or swipe first — used for
    // "show Tee 1/2's sizes as soon as the page loads" and for "auto-select
    // the first eligible Trucker on completing Tee 3" (selectCard()'s
    // existing branching already does the right thing for each: a
    // multi-variant tee/tank only reveals its size row without committing a
    // variant; a single "Default Title"-variant hat commits immediately).
    // Never silently adds anything to the cart by itself — it only ever
    // writes to `slots`/the DOM, same as a real tap would.
    //
    // Deliberately NOT used for Tee/Tank 3 — see previewTee3Sizes below for
    // why that one specific role needs a non-committing variant of this.
    function autoRevealFirstCard(key) {
      var rail = railEl(key);
      var card = rail && rail.querySelector('[data-oms-2tee-3tee-tee-card]');
      if (!card || card.disabled) return;
      selectCard(key, card);
    }

    // Tee/Tank 3 cannot reuse autoRevealFirstCard/selectCard the way
    // Tee 1/2 do: selectCard() writes slots.tee3.productId, and
    // inHatTier() — which gates the hat section's visibility AND which
    // reward-panel mode renders (confirmed live: the "--towel" mode with
    // its "Want the better freebie?" upsell prompt vs. "--hat" mode) —
    // reads exactly that field. Since selectVariant('tee2', ...) calls
    // render() synchronously before this preview ever runs, using
    // selectCard() here would flip inHatTier() true and jump straight to
    // "--hat" mode before the towel-tier upsell prompt ever paints —
    // skipping it entirely for every customer, confirmed by tracing the
    // render() call order. This shows the first card's sizes exactly like
    // selectCard()'s multi-variant path does, purely visually (via
    // renderVariantReveal directly), without writing slots.tee3 at all.
    // selectVariant() below backfills productId/title itself the moment a
    // size is actually tapped from this state, so "not committed until a
    // size is picked" still holds — same rule as every other role, just
    // implemented one step later for this one because of what its
    // productId alone already triggers elsewhere.
    function previewTee3Sizes() {
      var rail = railEl('tee3');
      var card = rail && rail.querySelector('[data-oms-2tee-3tee-tee-card]');
      if (!card || card.disabled) return;
      var variants = JSON.parse(card.getAttribute('data-variants') || '[]');
      renderVariantReveal('tee3', variants, card);
    }

    function selectCard(key, card) {
      var productId = card.getAttribute('data-product-id');
      var variants = JSON.parse(card.getAttribute('data-variants') || '[]');
      var changed = slots[key].productId !== productId;

      // Commit now runs every animation frame while this role's rail is
      // scrolling (see setupCoverflow), so the SAME still-centered card can
      // be "re-committed" many times during one swipe/settle. Once that
      // card is already this role's committed product, treat it as a
      // no-op — the earlier version always reset `slots[key].variant` to
      // null here unconditionally, which under per-frame commits would
      // have wiped out an already-chosen size on every subsequent frame
      // the same card stayed centered. Selecting/resetting only happens on
      // an actual product change.
      if (!changed) {
        railEl(key).querySelectorAll('.oms-2tee-3tee-tee-card').forEach(function (c) {
          c.classList.toggle('is-selected', c === card);
        });
        return;
      }

      slots[key].productId = productId;
      slots[key].variant = null;
      slots[key].title = card.querySelector('.oms-2tee-3tee-tee-card__title')?.textContent || '';

      railEl(key).querySelectorAll('.oms-2tee-3tee-tee-card').forEach(function (c) {
        c.classList.toggle('is-selected', c === card);
      });

      var onlyVariant = variants.length === 1 ? variants[0] : null;
      if (onlyVariant && onlyVariant.title === 'Default Title') {
        var reveal = document.getElementById('oms-2tee-3tee-' + key + '-variant-reveal-' + sectionId);
        if (reveal) reveal.hidden = true;
        selectVariant(key, onlyVariant, card);
        return;
      }

      renderVariantReveal(key, variants, card);
      // (For key === 'tee3' this also enters the hat tier for the first
      // time, since inHatTier() reads slots.tee3.productId, already set
      // above — render() picks that up and reveals the hat step.)
      render();
    }

    function renderVariantReveal(key, variants, card) {
      var reveal = document.getElementById('oms-2tee-3tee-' + key + '-variant-reveal-' + sectionId);
      var label = document.getElementById('oms-2tee-3tee-' + key + '-variant-label-' + sectionId);
      if (!reveal || !label) return;
      var title = card.querySelector('.oms-2tee-3tee-tee-card__title')?.textContent || '';
      label.textContent = title + ' — choose your size';
      reveal.hidden = false;
      renderVariantRow(key, variants, card);
    }

    function renderVariantRow(key, variants, card) {
      var row = root.querySelector('[data-oms-2tee-3tee-size-row="' + key + '"]');
      if (!row) return;
      row.innerHTML = '';
      variants.forEach(function (variant) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'oms-2tee-3tee-size-pill';
        btn.textContent = variant.title;
        btn.disabled = !variant.available;
        if (slots[key].variant && slots[key].variant.id === variant.id) {
          btn.classList.add('oms-2tee-3tee-size-pill--selected');
        }
        if (!variant.available) {
          btn.classList.add('oms-2tee-3tee-size-pill--sold-out');
          btn.title = 'Sold out';
        }
        btn.addEventListener('click', function () {
          selectVariant(key, variant, card);
        });
        row.appendChild(btn);
      });
    }

    function selectVariant(key, variant, card) {
      // Captured before the mutation below so it reflects whether Tee/Tank
      // 3 was ALREADY fully chosen (product + size) before this call —
      // compared against the post-mutation state to detect a genuine
      // "customer just finished Tee 3" transition. Gating on the full
      // variant (not just the product) matches the required order exactly:
      // the hat only auto-selects once all three tees are actually done.
      // This also naturally re-arms after Skip, which nulls slots.tee3
      // entirely (see the comment there) — no separate flag needed.
      var wasTee3Complete = Boolean(slots.tee3.variant);
      // previewTee3Sizes() (see its own comment) reveals Tee 3's first
      // card's sizes WITHOUT writing slots.tee3.productId/title — so if
      // this size pill was reached from that preview state rather than
      // through a real tap on the card first (selectCard), those two
      // fields need backfilling here, at the point the customer actually
      // commits to a size. A no-op whenever selectCard already set them.
      var productId = card.getAttribute('data-product-id');
      if (slots[key].productId !== productId) {
        slots[key].productId = productId;
        slots[key].title = card.querySelector('.oms-2tee-3tee-tee-card__title')?.textContent || '';
        railEl(key).querySelectorAll('.oms-2tee-3tee-tee-card').forEach(function (c) {
          c.classList.toggle('is-selected', c === card);
        });
      }
      slots[key].variant = variant;
      var variants = JSON.parse(card.getAttribute('data-variants') || '[]');
      renderVariantRow(key, variants, card);
      render();
      advanceAfterLock(key);
      // First time the towel tier completes, immediately reveal Tee/Tank
      // 3's first (centered) card's sizes — see the comment on
      // tee3AutoRevealed for why this is a one-time flag rather than a
      // transition check like the hat tier's.
      if (!tee3AutoRevealed && (key === 'tee1' || key === 'tee2') && towelTierComplete()) {
        tee3AutoRevealed = true;
        previewTee3Sizes();
      }
      // Tee 3 just went from incomplete to complete — enter the hat tier
      // with its first eligible Trucker already selected (intentional per
      // the offer design: the hat role's product always has a single
      // free-gift variant, so "revealed" and "selected" are the same
      // thing for it, unlike the tee roles above).
      if (key === 'tee3' && !wasTee3Complete && Boolean(slots.tee3.variant)) {
        autoRevealFirstCard('hat');
      }
    }

    function advanceAfterLock(key) {
      var behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      var order = { tee1: 'tee2', tee3: 'hat' };
      var nextKey = order[key];
      if (nextKey && slots[key].variant) {
        var target = nextKey === 'hat' ? hatSectionEl : document.getElementById('oms-2tee-3tee-' + nextKey + '-section-' + sectionId);
        target?.scrollIntoView({ behavior: behavior, block: 'start' });
      }
    }

    ALL_ROLES.forEach(function (key) {
      setupCoverflow(key);
    });

    // Tee 1 and Tee 2 are visible (and their first card centered) from the
    // moment the page loads, so their size options should be too — no
    // "once reachable" gate needed like tee3/hat below, which only become
    // relevant partway through the flow (see tee3AutoRevealed and the
    // wasTee3Complete check in selectVariant).
    autoRevealFirstCard('tee1');
    autoRevealFirstCard('tee2');

    root.querySelectorAll('[data-oms-2tee-3tee-rail-arrow]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = btn.getAttribute('data-oms-2tee-3tee-rail-target');
        var rail = railEl(target);
        var card = rail.querySelector('[data-oms-2tee-3tee-tee-card]');
        if (!card) return;
        var amount = card.getBoundingClientRect().width + 14;
        rail.scrollBy({
          left: btn.getAttribute('data-oms-2tee-3tee-rail-arrow') === 'prev' ? -amount : amount,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      });
    });

    ALL_ROLES.forEach(function (key) {
      var rail = railEl(key);
      if (!rail) return;
      var wrap = rail.closest('.oms-2tee-3tee-rail-wrap');
      function updateArrows() {
        var prev = wrap.querySelector('[data-oms-2tee-3tee-rail-arrow="prev"]');
        var next = wrap.querySelector('[data-oms-2tee-3tee-rail-arrow="next"]');
        var maxScroll = rail.scrollWidth - rail.clientWidth;
        if (prev) prev.disabled = rail.scrollLeft <= 1;
        if (next) next.disabled = maxScroll <= 1 || rail.scrollLeft >= maxScroll - 1;
      }
      rail.addEventListener('scroll', updateArrows, { passive: true });
      updateArrows();
      // Re-run a few times after page-init, and again on any later resize.
      // This is a real, confirmed bug, not a theoretical one: on QA, the
      // tee3 rail's "next" arrow was left permanently disabled from page
      // load even though rail.scrollWidth/clientWidth reported plenty of
      // scrollable content (matching tee1/tee2, whose arrows computed
      // correctly at the same instant). Manually re-running this exact
      // updateArrows() formula against the same element always produces the
      // correct (enabled) result once the page has settled — confirmed by
      // dispatching a synthetic 'scroll' event ~800ms after load, which
      // correctly flipped disabled from true to false — so the initial,
      // synchronous call at page-init is reading rail.scrollWidth/
      // clientWidth (or scrollLeft) before something further down the page
      // finishes settling, and nothing else naturally re-triggers
      // updateArrows() afterward: no scroll ever fires (the disabled arrow
      // itself prevents the customer from generating one), so once wrong it
      // silently stays wrong for the rest of the session, permanently
      // blocking that arrow. A single requestAnimationFrame pair (~2
      // frames, ~32ms) was tried first and was NOT enough to fix it in QA,
      // so this uses a short series of setTimeout re-checks spread further
      // out to reliably land after whatever is still settling; the
      // ResizeObserver additionally re-checks on any later size change
      // (e.g. viewport resize) so this can't silently go stale again after
      // that.
      [0, 150, 400, 900].forEach(function (delay) {
        setTimeout(updateArrows, delay);
      });
      if (window.ResizeObserver) {
        new ResizeObserver(updateArrows).observe(rail);
      }
    });

    var skipBtn = root.querySelector('[data-oms-2tee-3tee-skip-to-towel]');
    if (skipBtn) {
      skipBtn.addEventListener('click', function () {
        slots.tee3 = { productId: null, variant: null, title: null };
        slots.hat = { productId: null, variant: null, title: null };
        // Nulling slots.tee3 here is also what re-arms the hat tier's
        // auto-select for next time: selectVariant()'s wasTee3Complete
        // check reads Boolean(slots.tee3.variant) before and after each
        // Tee 3 size pick, so the next time the customer fully completes
        // Tee 3 again, it sees a false → true transition and re-selects
        // the first Trucker — no separate flag to reset here.
        // Suspend BEFORE the DOM class cleanup below: removing
        // is-focused/is-selected doesn't touch a rail's own hasInteracted
        // flag, so without this a commit already queued from the tee3 or
        // hat rail's still-settling scroll (see the comment on
        // railControllers in setupCoverflow) would otherwise land after
        // this handler returns and silently re-select the card the
        // customer just backed out of, undoing this reset a moment later.
        if (railControllers.tee3) railControllers.tee3.suspend();
        if (railControllers.hat) railControllers.hat.suspend();
        railEl('tee3')?.querySelectorAll('.oms-2tee-3tee-tee-card').forEach(function (c) { c.classList.remove('is-selected', 'is-focused'); });
        railEl('hat')?.querySelectorAll('.oms-2tee-3tee-tee-card').forEach(function (c) { c.classList.remove('is-selected', 'is-focused'); });
        var tee3Reveal = document.getElementById('oms-2tee-3tee-tee3-variant-reveal-' + sectionId);
        if (tee3Reveal) tee3Reveal.hidden = true;
        render();
        document.getElementById('oms-2tee-3tee-reward-panel-' + sectionId)?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      });
    }

    function nameWithOption(slot) {
      if (!slot.variant) return '';
      if (slot.variant.title === 'Default Title') return slot.title;
      return slot.title + ' · ' + slot.variant.title;
    }

    // No pending-price placeholder: per direction, the pricing box simply
    // doesn't render at all until real numbers are confirmed, rather than
    // showing a "pricing pending" badge in its place.
    function priceBlockHtml() {
      if (!labels.pricingConfirmed) return '';
      return '<div class="oms-2tee-3tee-summary__price-block">' +
        '<span class="oms-2tee-3tee-summary__price-amount">' + escapeHtml(labels.priceDisplay || '') + '</span>' +
        '<span class="oms-2tee-3tee-summary__price-saving">' + escapeHtml(labels.savingDisplay || '') + '</span></div>';
    }

    // ---------------------------------------------------------------------
    // Reward panel: entirely rewritten per render() call based on tier.
    // Only ever shows ONE reward — never towel + hat together.
    function renderRewardPanel() {
      if (!rewardPanelEl) return;

      if (!towelTierComplete()) {
        rewardPanelEl.hidden = true;
        rewardPanelEl.innerHTML = '';
        return;
      }

      rewardPanelEl.hidden = false;

      if (!inHatTier()) {
        rewardPanelEl.className = 'oms-2tee-3tee-reward-panel oms-2tee-3tee-reward-panel--towel';
        rewardPanelEl.innerHTML =
          '<div class="oms-2tee-3tee-reward-panel__row">' +
          (towel.image ? '<span class="oms-2tee-3tee-reward-panel__image"><img src="' + escapeHtml(towel.image) + '" alt="' + escapeHtml(towel.title || 'Gym Towel') + '" loading="lazy"></span>' : '') +
          '<div><p class="oms-2tee-3tee-reward-panel__kicker">Unlocked</p>' +
          '<h3 class="oms-2tee-3tee-reward-panel__heading">' + escapeHtml(labels.towelUnlockedHeading || 'FREE Gym Towel Unlocked!') + '</h3>' +
          '<p class="oms-2tee-3tee-reward-panel__copy">' + escapeHtml(labels.towelUnlockedCopy || (towel.title || 'OMS Microfibre Gym Towel') + ' — automatically added when you add your bundle.') + '</p></div>' +
          '</div>' +
          '<div class="oms-2tee-3tee-reward-panel__upsell">' +
          '<h4 class="oms-2tee-3tee-reward-panel__upsell-heading">' + escapeHtml(labels.upgradePromptHeading || 'Want the better freebie?') + '</h4>' +
          '<p class="oms-2tee-3tee-reward-panel__upsell-copy">' + escapeHtml(labels.upgradePromptCopy || '') + '</p>' +
          '<button type="button" class="oms-2tee-3tee-btn" data-oms-2tee-3tee-upsell-cta>' + escapeHtml(labels.upgradeCtaLabel || '+ Add 3rd Tee/Tank') + '</button>' +
          '</div>';
        var upsellBtn = rewardPanelEl.querySelector('[data-oms-2tee-3tee-upsell-cta]');
        if (upsellBtn) {
          upsellBtn.addEventListener('click', function () {
            // This button only ever renders while !inHatTier() — i.e.
            // Tee 3 is guaranteed unselected whenever it's clickable — so
            // it's always safe to (re-)preview the first card's sizes
            // here, harmless if selectVariant's own one-time trigger
            // already did it moments ago. This is what makes Tee 3's
            // preview reappear after a Skip → re-click cycle, the same
            // way the hat tier's own auto-select re-arms itself.
            previewTee3Sizes();
            tee3SectionEl?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
          });
        }
        return;
      }

      // Hat tier active.
      rewardPanelEl.className = 'oms-2tee-3tee-reward-panel oms-2tee-3tee-reward-panel--hat';
      var hatDone = Boolean(slots.hat.variant);
      var hatCopy = hatDone ? (labels.hatActiveDoneCopy || 'Your free hat is locked in.') : (labels.hatActivePendingCopy || 'Choose your free hat below.');
      var hatImg = hatDone ? railEl('hat')?.querySelector('.oms-2tee-3tee-tee-card.is-selected .oms-2tee-3tee-tee-card__image img') : null;
      rewardPanelEl.innerHTML =
        '<div class="oms-2tee-3tee-reward-panel__row">' +
        (hatImg ? '<span class="oms-2tee-3tee-reward-panel__image"><img src="' + escapeHtml(hatImg.src) + '" alt=""></span>' : '') +
        '<div><p class="oms-2tee-3tee-reward-panel__kicker">' + (hatDone ? 'Locked in' : 'Upgraded') + '</p>' +
        '<h3 class="oms-2tee-3tee-reward-panel__heading">' + escapeHtml(labels.hatActiveHeading || 'FREE Trucker Hat') + '</h3>' +
        '<p class="oms-2tee-3tee-reward-panel__copy">' + escapeHtml(hatCopy) + '</p></div>' +
        '</div>' +
        '<p class="oms-2tee-3tee-reward-panel__note">' + escapeHtml(labels.towelNoLongerCopy || 'Gym Towel no longer included at this tier.') + '</p>';
    }

    function render() {
      var hat = inHatTier();
      if (tee3SectionEl) tee3SectionEl.hidden = false; // upgrade step is always reachable once towel tier is complete-eligible
      if (hatSectionEl) hatSectionEl.hidden = !hat;

      renderRewardPanel();

      var rows = [
        { label: labels.tee1, value: slots.tee1.variant ? nameWithOption(slots.tee1) : (slots.tee1.productId ? labels.pickSize : labels.notSelected), status: slots.tee1.variant ? 'done' : 'pending', hidden: false },
        { label: labels.tee2, value: slots.tee2.variant ? nameWithOption(slots.tee2) : (slots.tee2.productId ? labels.pickSize : labels.notSelected), status: slots.tee2.variant ? 'done' : 'pending', hidden: false },
        { label: labels.tee3, value: slots.tee3.variant ? nameWithOption(slots.tee3) : (slots.tee3.productId ? labels.pickSize : labels.notSelected), status: slots.tee3.variant ? 'done' : 'pending', hidden: !hat },
        { label: labels.hat, value: slots.hat.variant ? nameWithOption(slots.hat) : (slots.hat.productId ? labels.pickOption : labels.notSelected), status: slots.hat.variant ? 'done' : 'pending', hidden: !hat },
        { label: labels.towel, value: labels.included, status: 'included', hidden: hat },
      ];

      var readyLabel = hasExistingBundleLines ? 'Update Bundle' : labels.ctaLabel;
      var ctaLabel = cartInProgress ? labels.ctaLoading : (isReady() ? readyLabel : labels.ctaIncomplete);
      var ready = isReady();

      var rowsHtml = rows.map(function (r) {
        return '<li class="oms-2tee-3tee-summary__row oms-2tee-3tee-summary__row--' + r.status + '"' + (r.hidden ? ' hidden' : '') + '>' +
          '<span class="oms-2tee-3tee-summary__label">' + escapeHtml(r.label) + '</span>' +
          '<span class="oms-2tee-3tee-summary__value">' + escapeHtml(r.value) + '</span></li>';
      }).join('');

      if (summaryEl) {
        summaryEl.innerHTML =
          '<h3 class="oms-2tee-3tee-summary__heading">' + escapeHtml(labels.summaryHeading) + '</h3>' +
          '<ul class="oms-2tee-3tee-summary__list">' + rowsHtml + '</ul>' +
          priceBlockHtml() +
          '<p class="oms-2tee-3tee-summary__error" data-oms-2tee-3tee-error' + (currentError ? '' : ' hidden') + '>' + escapeHtml(currentError) + '</p>' +
          '<button type="button" class="oms-2tee-3tee-btn oms-2tee-3tee-cta" aria-disabled="' + (!ready) + '">' + escapeHtml(ctaLabel) + '</button>';
      }

      if (stickyBarEl) {
        // Tee progress and the unlocked-reward name are visually distinct
        // (a plain dot-trail vs. an orange "chip") purely via the two
        // classes below — same data, same source, no new state.
        var stepParts = [
          (slots.tee1.variant ? '✓' : '○') + '&nbsp;1',
          (slots.tee2.variant ? '✓' : '○') + '&nbsp;2',
        ];
        var rewardName;
        if (hat) {
          stepParts.push((slots.tee3.variant ? '✓' : '○') + '&nbsp;3');
          rewardName = slots.hat.variant ? 'Trucker&nbsp;✓' : 'Pick&nbsp;Trucker';
        } else {
          rewardName = 'Towel&nbsp;✓';
        }
        var status = '<span class="oms-2tee-3tee-sticky-bar__steps">' + stepParts.join('&nbsp;&nbsp;') + '</span>' +
          '<span class="oms-2tee-3tee-sticky-bar__reward-chip">' + rewardName + '</span>';
        // No "pricing pending" placeholder here either — the price line is
        // simply omitted from the info block until pricing is confirmed,
        // matching priceBlockHtml() above.
        var stickyPriceHtml = labels.pricingConfirmed
          ? '<span class="oms-2tee-3tee-sticky-bar__price">' + escapeHtml(labels.priceDisplay || '') + ' · ' + escapeHtml(labels.savingDisplay || '') + '</span>'
          : '';
        stickyBarEl.innerHTML =
          '<div class="oms-2tee-3tee-sticky-bar__info">' +
          stickyPriceHtml +
          '<span class="oms-2tee-3tee-sticky-bar__status">' + status + '</span></div>' +
          '<button type="button" class="oms-2tee-3tee-btn oms-2tee-3tee-cta" aria-disabled="' + (!ready) + '">' + escapeHtml(ctaLabel) + '</button>';
      }

      [summaryEl, stickyBarEl].forEach(function (container) {
        if (!container) return;
        container.querySelectorAll('.oms-2tee-3tee-cta').forEach(function (btn) {
          btn.addEventListener('click', handleAddBundle);
        });
      });
    }

    function nudgeIncomplete() {
      var missing;
      if (inHatTier()) {
        missing = !slots.tee1.variant ? 'tee1' : !slots.tee2.variant ? 'tee2' : !slots.tee3.variant ? 'tee3' : !slots.hat.variant ? 'hat' : null;
      } else {
        missing = !slots.tee1.variant ? 'tee1' : !slots.tee2.variant ? 'tee2' : null;
      }
      if (missing) {
        var target = missing === 'hat' ? hatSectionEl : document.getElementById('oms-2tee-3tee-' + missing + '-section-' + sectionId);
        target?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    }

    // Error is stored as state and read by render() when it builds the
    // markup, rather than written directly into the DOM — a direct DOM
    // write would be silently discarded by the very next render() call
    // (always invoked right after, in handleAddBundle's finally), which
    // rebuilds summaryEl.innerHTML from scratch.
    var currentError = '';
    function showError(message) {
      currentError = message || '';
    }

    // Applies a reconciliation plan's adds. Split out of
    // reconcileToDesired() only so that function reads as one clear
    // fetch → diff → apply → re-verify loop.
    async function applyAdds(addItems) {
      if (addItems.length === 0) return;
      if (window.Unicorn && typeof window.Unicorn.addItemsToCart === 'function') {
        var result = await window.Unicorn.addItemsToCart({ items: addItems });
        if (result && result.status && result.status >= 400) {
          throw new Error(typeof result.errors === 'string' ? result.errors : 'Could not update your bundle in cart.');
        }
      } else {
        // Unicorn unavailable — least-invasive Shopify-native fallback only.
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: addItems }),
        });
        if (!res.ok) {
          var errBody = await res.json().catch(function () { return {}; });
          throw new Error(errBody.message || errBody.description || 'Could not update your bundle in cart.');
        }
      }
    }

    // Drives the real Shopify cart to match `desired`, using the exact same
    // fresh-cart → diff (planReconciliation) → add-required-first →
    // remove-stale-after sequence as before, but as a bounded loop instead
    // of a single fire-and-forget pass.
    //
    // A single pass is not reliable for a tier downgrade specifically
    // because of how Shopify's cart line `key` behaves once an automatic
    // discount is allocated to that line (exactly what happens to the hat
    // line the moment it enters the hat tier): confirmed by direct testing,
    // a `key` read from a fresh /cart.js for a just-discounted line can
    // already be stale by the time it's used a moment later in a
    // /cart/change.js call — Shopify returns 400 "no valid id or line
    // parameter" — even with nothing else touching the cart in between.
    // Which specific line's key goes stale isn't predictable up front, so
    // the fix isn't a fixed delay or a special case for the hat role: this
    // re-fetches the cart and re-runs planReconciliation() against the
    // SAME `desired` after every pass (bounded, so a genuinely broken
    // request still surfaces as an error instead of looping forever), and
    // — critically — a removal/quantity-fix failing on one pass is
    // swallowed rather than left to abort the whole reconciliation, so the
    // next pass's fresh fetch picks up a current key and actually
    // completes it. Every pass is still scoped solely to this bundle's own
    // tagged roles via planReconciliation/collectBundleLines — never a
    // blind clear, and a customer's own independently-added products (or
    // any other campaign's lines) are never touched.
    var MAX_RECONCILE_ATTEMPTS = 3;
    async function reconcileToDesired(desired) {
      for (var attempt = 0; attempt < MAX_RECONCILE_ATTEMPTS; attempt++) {
        var cart = await fetchCart();
        var plan = planReconciliation(cart, desired);

        if (plan.addItems.length === 0 && plan.removeKeys.length === 0 && plan.fixQuantityKeys.length === 0) {
          return; // Converged: cart already matches `desired` for every bundle-owned role.
        }

        // Adds run before removes/quantity-fixes on purpose, and are NOT
        // caught here: if a new line fails to add (sold out, a per-order
        // quantity limit, network error, etc.), the customer must keep
        // whatever correct bundle line they already had rather than being
        // left with that role missing entirely, so this throws straight
        // out to handleAddBundle's error handling instead of retrying —
        // this also protects a tier downgrade, since we never drop the old
        // tier's lines until the new tier's lines are confirmed added.
        await applyAdds(plan.addItems);

        // Removes/quantity-fixes, unlike adds, target a specific existing
        // line by its `key` — exactly what can go stale mid-pass (see
        // above). A failure here is deliberately swallowed rather than
        // propagated: the next loop iteration re-fetches the cart and
        // recomputes the plan (and every `key` in it) from scratch, which
        // is what actually resolves it.
        try {
          for (var i = 0; i < plan.removeKeys.length; i++) {
            await changeCartLine(plan.removeKeys[i], 0);
          }
          for (var j = 0; j < plan.fixQuantityKeys.length; j++) {
            await changeCartLine(plan.fixQuantityKeys[j], 1);
          }
        } catch (staleLineErr) {
          // Swallowed on purpose — see comment above. Falls through to the
          // next attempt's fresh fetch + re-plan.
        }
      }

      // Exhausted retries — verify one final time and surface a clear
      // error rather than silently leaving stale/incorrect bundle lines in
      // the customer's cart.
      var finalCart = await fetchCart();
      var finalPlan = planReconciliation(finalCart, desired);
      if (finalPlan.addItems.length > 0 || finalPlan.removeKeys.length > 0 || finalPlan.fixQuantityKeys.length > 0) {
        throw new Error('Could not fully sync your bundle in cart. Please refresh and try again.');
      }
    }

    // Reconciles the customer's CURRENT tier selections against the CURRENT
    // Shopify cart (fetched fresh, never assumed) every time this runs.
    // Never a blind re-add: only the lines that actually need to change are
    // touched, identified solely by this bundle's own _oms_3tee_bundle /
    // _oms_3tee_role properties — a customer's own independently-added
    // products (and any other campaign's lines) are never inspected or
    // altered.
    async function handleAddBundle() {
      if (!isReady()) {
        nudgeIncomplete();
        return;
      }

      cartInProgress = true;
      showError('');
      render();

      try {
        await reconcileToDesired(desiredSelection());

        // addItemsToCart marks its own request so Unicorn's fetch-observer skips it,
        // leaving the drawer showing pre-update state until explicitly refreshed.
        if (window.Unicorn && typeof window.Unicorn.refreshCart === 'function') {
          await window.Unicorn.refreshCart();
        }
        if (window.Unicorn && typeof window.Unicorn.openCart === 'function') {
          window.Unicorn.openCart();
        } else if (typeof window.unicornOpenCart === 'function') {
          window.unicornOpenCart();
        } else if (window.Shopify && window.Shopify.actions && window.Shopify.actions.openCart) {
          window.Shopify.actions.openCart();
        } else {
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        }

      } catch (err) {
        showError('Something went wrong updating your bundle. Please try again.');
        if (window.console && console.error) console.error('[OMS 3-Tee]', err);
      } finally {
        cartInProgress = false;
        render();
        // Re-check against a fresh /cart.js rather than assuming success set
        // this permanently true — a customer can remove every bundle line
        // via Unicorn's own cart UI at any time, entirely outside this
        // function, and the CTA label must not lie about that.
        refreshBundleLinePresence();
      }
    }

    render();
    refreshBundleLinePresence();
  }

  function initSizeGuideModal(root) {
    // Pre-existing bug, unrelated to today's changes: the modal markup is a
    // sibling AFTER the section closes (same as the image-zoom modal below),
    // not a descendant of `root` — root.querySelector() here always
    // returned null, so this modal has never actually opened. Matching
    // initImageZoomModal's document.getElementById lookup (scoped by
    // section id, same as that function) fixes it.
    var sectionId = root.id.replace('oms-2tee-3tee-builder-', '');
    var modal = document.getElementById('oms-2tee-3tee-size-guide-modal-' + sectionId);
    var trigger = root.querySelector('[data-oms-2tee-3tee-size-guide-trigger]');
    if (!modal || !trigger || modal.dataset.oms2tee3teeInit) return;
    modal.dataset.oms2tee3teeInit = 'true';

    var img = modal.querySelector('[data-oms-2tee-3tee-lightbox-img]');
    var closeBtn = modal.querySelector('[data-oms-2tee-3tee-lightbox-close]');
    var lastFocused = null;

    function onKeydown(e) {
      if (e.key === 'Escape') {
        close();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        closeBtn.focus();
      }
    }

    function open() {
      var srcImg = trigger.querySelector('[data-oms-2tee-3tee-size-guide-src]');
      img.src = srcImg ? srcImg.getAttribute('data-oms-2tee-3tee-size-guide-src') : srcImg.src;
      img.alt = srcImg ? srcImg.alt : 'Size guide';
      modal.hidden = false;
      document.body.classList.add('oms-2tee-3tee-modal-open');
      lastFocused = trigger;
      closeBtn.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      modal.hidden = true;
      document.body.classList.remove('oms-2tee-3tee-modal-open');
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused) lastFocused.focus();
    }

    trigger.addEventListener('click', open);
    modal.addEventListener('click', close);
    modal.querySelector('.oms-2tee-3tee-lightbox__dialog').addEventListener('click', function (e) {
      e.stopPropagation();
    });
    closeBtn.addEventListener('click', close);
  }

  // Product image zoom lightbox — deliberately standalone, with no
  // reference to `slots`, cart, or reconciliation state anywhere in this
  // function. Cards call `modal._omsOpen(images, triggerEl)` (see the
  // zoom-trigger click handler in setupCoverflow) to open it; everything
  // else here only ever manipulates this modal's own DOM.
  function initImageZoomModal(root) {
    var sectionId = root.id.replace('oms-2tee-3tee-builder-', '');
    var modal = document.getElementById('oms-2tee-3tee-image-zoom-modal-' + sectionId);
    if (!modal || modal.dataset.oms2tee3teeZoomInit) return;
    modal.dataset.oms2tee3teeZoomInit = 'true';

    var dialog = modal.querySelector('.oms-2tee-3tee-lightbox__dialog');
    var imgEl = modal.querySelector('[data-oms-2tee-3tee-zoom-image]');
    var closeBtn = modal.querySelector('[data-oms-2tee-3tee-zoom-close]');
    var prevBtn = modal.querySelector('[data-oms-2tee-3tee-zoom-prev]');
    var nextBtn = modal.querySelector('[data-oms-2tee-3tee-zoom-next]');
    var counterEl = modal.querySelector('[data-oms-2tee-3tee-zoom-counter]');

    var images = [];
    var index = 0;
    var lastFocused = null;
    var touchStartX = null;
    var touchStartY = null;

    function renderImage() {
      var current = images[index];
      if (!current) return;
      imgEl.src = current.src;
      imgEl.alt = current.alt || '';
      var multi = images.length > 1;
      prevBtn.hidden = !multi;
      nextBtn.hidden = !multi;
      counterEl.hidden = !multi;
      if (multi) counterEl.textContent = (index + 1) + ' / ' + images.length;
    }

    function show(delta) {
      if (images.length < 2) return;
      index = (index + delta + images.length) % images.length;
      renderImage();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        close();
      } else if (e.key === 'ArrowLeft') {
        show(-1);
      } else if (e.key === 'ArrowRight') {
        show(1);
      } else if (e.key === 'Tab') {
        e.preventDefault();
        closeBtn.focus();
      }
    }

    function open(imageList, triggerEl) {
      if (!imageList || imageList.length === 0) return;
      images = imageList;
      index = 0;
      renderImage();
      modal.hidden = false;
      document.body.classList.add('oms-2tee-3tee-modal-open');
      lastFocused = triggerEl || document.activeElement;
      closeBtn.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      modal.hidden = true;
      document.body.classList.remove('oms-2tee-3tee-modal-open');
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    modal.addEventListener('click', close);
    dialog.addEventListener('click', function (e) { e.stopPropagation(); });
    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', function (e) { e.stopPropagation(); show(-1); });
    nextBtn.addEventListener('click', function (e) { e.stopPropagation(); show(1); });

    // Swipe left/right to navigate. A predominantly-vertical drag is
    // ignored so it reads as a scroll/dismiss gesture rather than
    // accidentally flipping images.
    dialog.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    dialog.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var dx = e.changedTouches[0].clientX - touchStartX;
      var dy = e.changedTouches[0].clientY - touchStartY;
      touchStartX = null;
      touchStartY = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      show(dx < 0 ? 1 : -1);
    }, { passive: true });

    modal._omsOpen = open;
  }

  function initAll(container) {
    (container || document).querySelectorAll('[data-oms-2tee-3tee-builder]').forEach(function (root) {
      initBuilder(root);
      initSizeGuideModal(root);
      initImageZoomModal(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
